/**
 * IAD Custom Function
 * Mirrors the C# InSyncAddinMethod.IAD key-building and cache semantics.
 */

import { DataStore, USE_API_DOC_KEY_FORMAT } from './dataStore';
import { createLogger } from '../utils/logger';
import { config, IS_DEVELOPING } from '../utils/config';
import { tokenStorage } from '../core/auth/tokenStorage';
import { recordAddinActivity } from '../core/auth/idleSession';
import { devLog } from '../utils/devLog';
import { readProbeToken } from '../store';
import { isWafBlocked, WAF_BLOCKED_MESSAGE } from '../utils/waf';
import { isSessionEnded, SESSION_ENDED_MESSAGE } from '../utils/sessionEnded';

declare const OfficeRuntime: any;

const logger = createLogger('IAD');

// A string is only treated as a date via the lenient JS Date parser if it
// contains a month name. This mirrors C# DateTime.TryParse rejecting free text
// (e.g. "Convert 1") that V8's `new Date()` would otherwise coerce to 1/1/2001.
const MONTH_NAME_RE = /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i;

const BATCH_DEBOUNCE_MS = 50;
// Smaller batches that can run in parallel beat large serial batches:
// the server's per-request latency dominates, so 4 × 2000 keys in parallel
// finishes far faster than 1 × 6000 → 1 × 6000 → 1 × 2332.
const MAX_BATCH_SIZE_LIMIT = 10000;
const DEFAULT_MAX_CONCURRENT_BATCHES = 100;
const BATCH_TIMEOUT_MS = 60000;
const NO_CACHE_MARKER = '|NO_IAD_CACHE';
const INSERT_CLICK_START_KEY = 'IAD_INSERT_CLICK_START_MS';
const BATCH_TRIGGER_CONTEXT_KEY = 'IAD_BATCH_TRIGGER_CONTEXT';
const DEV_TIMING_ENABLED = false;

type PendingResolver = {
  resolve: (value: any) => void;
  ltType?: string;
  dp?: any;
};

const batchQueue = new Map<string, PendingResolver[]>();
let batchTimer: ReturnType<typeof setTimeout> | undefined;
let inFlightBatchCount = 0;

// ============================================================================
// BATCH TIMING TRACKING
// ============================================================================
let batchSessionStartTime: number | null = null;
let totalBatchesStarted = 0;
let totalBatchesCompleted = 0;
let totalKeysFetched = 0;
let totalKeysMatched = 0;
let totalBatchFetchTime = 0;
let slowestBatchFetchTime = 0;
let sessionActive = false;
let currentBatchSize = 4000;
let currentMaxConcurrentBatches = DEFAULT_MAX_CONCURRENT_BATCHES;
let lastLoggedBatchPlan = '';
let activeInsertClickStart = '';
let totalKeysQueuedForInsertClick = 0;

function getCurrentInsertClickStart(): string {
  try {
    return localStorage.getItem(INSERT_CLICK_START_KEY) || '';
  } catch {
    return '';
  }
}

function getCurrentBatchTriggerContext(): string {
  try {
    return localStorage.getItem(BATCH_TRIGGER_CONTEXT_KEY) || 'unknown';
  } catch {
    return 'unknown';
  }
}

function chooseBatchPlan(workloadKeyCount: number): { batchSize: number; maxConcurrentBatches: number } {
  if (workloadKeyCount <= 30000) {
    return { batchSize: 4000, maxConcurrentBatches: 50 };
  }

  if (workloadKeyCount <= 90000) {
    return { batchSize: 7000, maxConcurrentBatches: 40 };
  }

  return { batchSize: MAX_BATCH_SIZE_LIMIT, maxConcurrentBatches: 25 };
}

// ============================================================================
// ISSUE 5 DIAGNOSTICS — "all cells stay #BUSY and no API calls happen"
// ----------------------------------------------------------------------------
// A cell shows #BUSY until its queued Promise resolves. If NO network call ever
// fires, the pipeline queued keys but never reached fetchBatch. There are only
// four places that can happen:
//   (a) Excel never re-invokes IAD_OFJ           → invokeCount flat
//   (b) keys queued but the flush timer never runs → queued>0, timerScheduled
//       true forever, batchesStarted flat (runtime/timer starvation)
//   (c) flush runs but concurrency is saturated   → inFlight >= maxConcurrent,
//       yet in-flight batches never complete (leaked inFlightBatchCount / wedge)
//   (d) fetch fired but hangs                     → httpCalls advanced, then
//       msSinceLastFetch grows without completion
// Every phase is tagged [IAD5] AND mirrored to localStorage(IAD_PIPELINE_STATE)
// so the state is readable from the task-pane DevTools even though the custom-
// functions console is a separate runtime. Flip IAD5_DIAGNOSTICS off to silence.
const IAD5_DIAGNOSTICS = true;
const IAD5_STATE_KEY = 'IAD_PIPELINE_STATE';
// Rolling log ("temp file" equivalent). The custom-functions runtime has NO OS
// filesystem access, but localStorage IS the proven shared channel here (same
// origin as the task pane — it carries the auth token and the DataStore mirror),
// so a ring buffer written here is readable from the task-pane DevTools console:
//   JSON.parse(localStorage.getItem('IAD_PIPELINE_LOG'))     ← the full timeline
//   JSON.parse(localStorage.getItem('IAD_PIPELINE_STATE'))   ← just the latest
const IAD5_LOG_KEY = 'IAD_PIPELINE_LOG';
const IAD5_COUNTERS_KEY = 'IAD_PIPELINE_COUNTERS';
const IAD5_LOG_MAX = 200;
let iad5LastFetchStartAt = 0;
let iad5TotalHttpCalls = 0;
let iad5StallWatchdog: ReturnType<typeof setInterval> | undefined;
let iad5KeysSeen = 0; // cumulative distinct keys that entered the queue
let iad5KeysResolved = 0; // cumulative keys resolved back to their cells
let iad5KeysInFlight = 0; // keys currently inside an active fetch

function iad5UpdateCounters(): void {
  if (!IAD5_DIAGNOSTICS) return;
  const counters = {
    at: new Date().toISOString(),
    invokeCount: ((globalThis as any).__INSYNC_IAD_INVOKE_COUNT__ as number) || 0,
    keysSeen: iad5KeysSeen,
    keysResolved: iad5KeysResolved,
    keysLeft: Math.max(0, iad5KeysSeen - iad5KeysResolved),
    keysInFlight: iad5KeysInFlight,
    queuedNow: batchQueue.size,
    inFlightBatches: inFlightBatchCount,
    httpCalls: iad5TotalHttpCalls,
    batchSize: currentBatchSize,
    maxConcurrent: currentMaxConcurrentBatches,
  };
  try {
    localStorage.setItem(IAD5_COUNTERS_KEY, JSON.stringify(counters));
  } catch {
    // quota — non-fatal
  }
}

function iad5AppendLog(entry: Record<string, unknown>): void {
  try {
    const raw = localStorage.getItem(IAD5_LOG_KEY);
    const arr: unknown[] = raw ? JSON.parse(raw) : [];
    arr.push(entry);
    // Cap to the most recent IAD5_LOG_MAX entries so the log can't grow unbounded.
    if (arr.length > IAD5_LOG_MAX) arr.splice(0, arr.length - IAD5_LOG_MAX);
    localStorage.setItem(IAD5_LOG_KEY, JSON.stringify(arr));
  } catch {
    // Quota exceeded (itself an issue-5 signal) or parse failure — non-fatal.
  }
}

function iad5Snapshot(phase: string) {
  return {
    phase,
    at: new Date().toISOString(),
    queued: batchQueue.size,
    inFlight: inFlightBatchCount,
    maxConcurrent: currentMaxConcurrentBatches,
    batchSize: currentBatchSize,
    timerScheduled: batchTimer !== undefined,
    sessionActive,
    batchesStarted: totalBatchesStarted,
    batchesCompleted: totalBatchesCompleted,
    httpCalls: iad5TotalHttpCalls,
    invokeCount: ((globalThis as any).__INSYNC_IAD_INVOKE_COUNT__ as number) || 0,
    msSinceLastFetch: iad5LastFetchStartAt ? Math.round(performance.now() - iad5LastFetchStartAt) : null,
  };
}

function iad5Log(phase: string, extra?: string): void {
  if (!IAD5_DIAGNOSTICS) return;
  const snap = extra ? { ...iad5Snapshot(phase), note: extra } : iad5Snapshot(phase);
  devLog(`[IAD5] ${phase}${extra ? ' | ' + extra : ''}`, snap);
  try {
    localStorage.setItem(IAD5_STATE_KEY, JSON.stringify(snap));
  } catch {
    // localStorage may be full (see issue 5 quota angle) — the console line still stands.
  }
  // …and append to the rolling timeline (the "temp file").
  iad5AppendLog(snap);
  iad5UpdateCounters();
}

// Convenience readers, available in whichever runtime can reach a console. The
// task pane reads the SAME localStorage, so from the task-pane console you can
// also just call JSON.parse(localStorage.getItem('IAD_PIPELINE_LOG')).
(globalThis as any).__IAD5_DUMP__ = (): Record<string, unknown> => {
  const read = (k: string, fallback: unknown) => {
    try {
      const raw = localStorage.getItem(k);
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  };
  return {
    counters: read(IAD5_COUNTERS_KEY, null),
    latest: read(IAD5_STATE_KEY, null),
    log: read(IAD5_LOG_KEY, []),
  };
};
// Live count of how many times Excel has invoked IAD_OFJ. Poll this after a full
// recalc (Ctrl+Alt+F9) to see whether Excel resumes invoking the deferred cells:
// if it climbs toward the number of formula cells, it was a calc-scheduling issue;
// if it stays flat, Excel is capping custom-function evaluation for this model.
(globalThis as any).__IAD5_INVOKES__ = (): number =>
  ((globalThis as any).__INSYNC_IAD_INVOKE_COUNT__ as number) || 0;
(globalThis as any).__IAD5_CLEAR__ = (): void => {
  try {
    localStorage.removeItem(IAD5_LOG_KEY);
    localStorage.removeItem(IAD5_STATE_KEY);
    localStorage.removeItem(IAD5_COUNTERS_KEY);
  } catch {
    // ignore
  }
};

// Fires only while work is outstanding; self-clears when the queue drains. If the
// queue has keys but no HTTP call started in the last 5s, it prints WHY.
function iad5StartStallWatchdog(): void {
  if (!IAD5_DIAGNOSTICS || iad5StallWatchdog !== undefined) return;
  iad5StallWatchdog = setInterval(() => {
    if (batchQueue.size === 0 && inFlightBatchCount === 0) {
      if (iad5StallWatchdog !== undefined) clearInterval(iad5StallWatchdog);
      iad5StallWatchdog = undefined;
      return;
    }
    const idleMs = iad5LastFetchStartAt ? performance.now() - iad5LastFetchStartAt : Infinity;
    if (batchQueue.size > 0 && idleMs > 5000) {
      const reason =
        inFlightBatchCount >= currentMaxConcurrentBatches
          ? `concurrency SATURATED (inFlight=${inFlightBatchCount} >= max=${currentMaxConcurrentBatches}) — in-flight batches are not completing`
          : batchTimer === undefined
            ? 'no flush scheduled and no batch running — timer/runtime STARVED or flush early-returned'
            : 'flush scheduled but batches not starting';
      iad5Log('STALL', `${Number.isFinite(idleMs) ? Math.round(idleMs) + 'ms' : 'never'} since last HTTP call — ${reason}`);
    }
  }, 3000);
}

function startBatchSession(): void {
  if (!sessionActive) {
    const clickStart = getCurrentInsertClickStart();
    if (clickStart !== activeInsertClickStart) {
      activeInsertClickStart = clickStart;
      totalKeysQueuedForInsertClick = 0;
    }

    batchSessionStartTime = performance.now();
    totalBatchesStarted = 0;
    totalBatchesCompleted = 0;
    totalKeysFetched = 0;
    totalKeysMatched = 0;
    totalBatchFetchTime = 0;
    slowestBatchFetchTime = 0;
    lastLoggedBatchPlan = '';
    sessionActive = true;
    devLog(`[IAD] [BATCH TIMING] Session started at ${new Date().toISOString()}`);
  }
}

function endBatchSession(): void {
  if (sessionActive && batchSessionStartTime) {
    const totalTime = performance.now() - batchSessionStartTime;
    const absoluteNow = performance.timeOrigin + performance.now();
    let totalSinceInsertClick: number | null = null;
    const triggerContext = getCurrentBatchTriggerContext();
    try {
      const rawClickStart = localStorage.getItem(INSERT_CLICK_START_KEY);
      const clickStart = rawClickStart ? Number(rawClickStart) : NaN;
      if (Number.isFinite(clickStart)) {
        totalSinceInsertClick = absoluteNow - clickStart;
      }
    } catch {}
    const keysPerSec = totalBatchesCompleted > 0
      ? (totalKeysFetched / (totalTime / 1000)).toFixed(0)
      : '0';
    devLog(`[TIMING][IAD] ========== FORMULA RESOLUTION SESSION COMPLETE ==========`);
    devLog(`[TIMING][IAD] Trigger context: ${triggerContext}`);
    devLog(`[TIMING][IAD] Formula resolution session time: ${totalTime.toFixed(2)}ms`);
    devLog(`[TIMING][IAD] Batches started: ${totalBatchesStarted}`);
    devLog(`[TIMING][IAD] Batches completed: ${totalBatchesCompleted}`);
    devLog(`[TIMING][IAD] Total formula keys requested: ${totalKeysFetched}`);
    devLog(`[TIMING][IAD] Total formula keys matched: ${totalKeysMatched}`);
    devLog(`[TIMING][IAD] Total API fetch time: ${totalBatchFetchTime.toFixed(2)}ms`);
    devLog(`[TIMING][IAD] Slowest batch fetch: ${slowestBatchFetchTime.toFixed(2)}ms`);
    devLog(`[TIMING][IAD] Average batch fetch: ${(totalBatchesCompleted > 0 ? totalBatchFetchTime / totalBatchesCompleted : 0).toFixed(2)}ms`);
    devLog(`[TIMING][IAD] Throughput: ${keysPerSec} keys/sec (max parallelism=${currentMaxConcurrentBatches}, batchSize=${currentBatchSize})`);
    if (totalSinceInsertClick !== null) {
      devLog(`[TIMING][IAD] Total formula resolution time since trigger click: ${totalSinceInsertClick.toFixed(2)}ms`);
    }
    devLog(`[TIMING][IAD] =======================================================`);
    sessionActive = false;
    batchSessionStartTime = null;
  }
}

/**
 * Edge-auth headers for same-origin data calls (VAPT V-001). Custom functions run
 * outside HttpClient, so they attach these themselves: the non-secret marker plus
 * the istari login token (read from the localStorage mirror the taskpane seeds).
 * nginx validates the token and injects the real backend credential — no credential
 * is present in the bundle. On no token the edge 401s and the caller degrades.
 */
async function getEdgeAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'X-Insync-Auth': 'dd' };
  try {
    const token = await tokenStorage.getEdgeToken();
    if (token) headers['X-Insync-Token'] = token.replace(/[\r\n]/g, '');
  } catch { /* no token -> edge 401 -> caller shows "No data" */ }
  return headers;
}

/**
 * X-Insync-User header for same-origin (proxied) requests so nginx can attribute
 * each API call to the logged-in user in the access log (`user_email`, surfaced
 * by the Grafana User Logs Dashboard). Custom functions run in a separate runtime
 * and bypass HttpClient, so they must add this themselves.
 *
 * NOTE: the custom-functions runtime gets its OWN OfficeRuntime.storage and cannot
 * read what the taskpane wrote at login — tokenStorage.getUsername() resolves this
 * through the shared localStorage mirror instead. Never blocks the request: on any
 * error it returns no header.
 */
async function getInsyncUserHeader(): Promise<Record<string, string>> {
  try {
    const user = await tokenStorage.getUsername();
    if (user) {
      return { 'X-Insync-User': user.replace(/[\r\n]/g, '').slice(0, 320) };
    }
  } catch {
    /* never block a data fetch on user attribution */
  }
  return {};
}

/**
 * InSync Analytics Data - Retrieves financial data and estimates.
 * @customfunction IAD_OFJ
 * @helpurl https://insyncanalytics.com/consensus/add-in/functions/#iad-ofj
 * @streaming
 * @param ticker Ticker symbol.
 * @param {string} [period] Fiscal period or metadata selector.
 * @param {string} [ltCode] Line item code.
 * @param {string} [ltType] Line item type or broker.
 * @param {string} [dp] Datapoint selector.
 * @param {CustomFunctions.StreamingInvocation<any>} invocation Streaming invocation used to update the cell.
 */
export function IAD_OFJ(
  ticker: string,
  period: string = '',
  ltCode: string = '',
  ltType: string = '',
  dp: string = '',
  invocation: CustomFunctions.StreamingInvocation<any>
): void {
  if (!invocation?.setResult) {
    console.warn('[IAD] Streaming invocation was not provided by Excel.');
    return;
  }

  // Evaluating an InSync formula counts as using the add-in (OTP idle timeout).
  recordAddinActivity();
  noteIadInvoke();

  let canceled = false;
  invocation.setResult('Loading...');
  invocation.onCanceled = () => {
    canceled = true;
  };

  if (IS_DEVELOPING && (ticker || '').trim().toUpperCase() === 'AUTHCHECK') {
    void (async () => {
      try {
        const mask = (t: string | null) => (t ? `${t.slice(0, 4)}…${t.slice(-3)}(len${t.length})` : 'absent');
        const stored = OfficeRuntime.storage.setItem("iad_test","test123")
        // (A) The probe the TASKPANE wrote on login, read via BARE-global
        // OfficeRuntime.storage + localStorage (store.ts). If probeOffice shows
        // "TASK…" the store IS shared across runtimes; if absent it is not.
        const probe = await readProbeToken();

        // (B) BARE-global OfficeRuntime.storage — write a CF marker, then list keys.
        const gStore = (typeof OfficeRuntime !== 'undefined' && OfficeRuntime) ? OfficeRuntime.storage : null;
        try { await gStore?.setItem('iad_cf_probe', 'CF'); } catch { /* */ }
        let gKeys: string[] = [];
        try { gKeys = gStore && gStore.getKeys ? await gStore.getKeys() : []; } catch { /* */ }

        // (C) window.OfficeRuntime.storage — what tokenStorage uses today.
        const wStore = (window as any)?.OfficeRuntime?.storage ?? null;
        let wKeys: string[] = [];
        try { wKeys = wStore && wStore.getKeys ? await wStore.getKeys() : []; } catch { /* */ }

        // (D) tokenStorage's own resolution.
        const enc = await tokenStorage.getUserTempToken();
        const filter = await tokenStorage.getUsernameFilter();
        const status = filter ? 'OK' : 'NOAUTH';

        if (!canceled) {
          invocation.setResult(
            `${status} refs=${probe.refs} probeOffice=${mask(probe.office)} probeLS=${mask(probe.ls)} ` +
            `gKeys=${gKeys.length}[${gKeys.slice(0, 6).join(',')}] wKeys=${wKeys.length}[${wKeys.slice(0, 6).join(',')}] ` +
            `enc=${mask(enc)} stored=${stored}`,
          );
        }
      } catch (e) {
        if (!canceled) invocation.setResult(`DIAGERR:${(e as Error).message}`);
      }
    })();
    return;
  }

  resolveIADValue(ticker, period, ltCode, ltType, dp)
    .then((result) => {
      if (!canceled) {
        invocation.setResult(result);
      }
    })
    .catch((error) => {
      console.error('[IAD] ERROR processing streaming request:', error);
      logger.error('IAD streaming function error', error as Error);
      if (!canceled) {
        invocation.setResult('-ERROR');
      }
    });
}

// [REFRESH] verification instrumentation. Per-formula logging would be thousands
// of lines, so accumulate hit/miss counts and flush a single debounced summary
// ~300ms after a burst settles. After a Refresh this is the proof of life: if a
// summary line appears, the formulas re-invoked (and how many re-fetched); if
// NONE appears, the recalc did not re-invoke them and nothing was re-fetched.
let iadEvalCount = 0;
let iadHitCount = 0;
let iadMissCount = 0;
let iadEvalSummaryTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

// Raw invocation counter — fires at IAD_OFJ entry, before any short-circuit, so it
// answers "did Excel call the function at all?" independently of cache hit/miss.
let iadInvokeCount = 0;
let iadInvokeSummaryTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

function noteIadInvoke(): void {
  iadInvokeCount++;
  // Topology probe: bump a counter on globalThis. If the task pane (refreshService)
  // can read this same counter climbing, the task pane and the custom-functions
  // code share ONE globalThis (one runtime) — which decides how Refresh can signal
  // the functions. If it stays 0 in the task pane even after IAD_OFJ clearly ran,
  // they are separate runtimes and we need a storage-based signal instead.
  (globalThis as any).__INSYNC_IAD_INVOKE_COUNT__ =
    (((globalThis as any).__INSYNC_IAD_INVOKE_COUNT__ as number) || 0) + 1;

  if (iadInvokeSummaryTimer === undefined) {
    iadInvokeSummaryTimer = globalThis.setTimeout(() => {
      devLog(`[IAD] Excel invoked IAD_OFJ ${iadInvokeCount} time${iadInvokeCount === 1 ? '' : 's'}`);
      iadInvokeCount = 0;
      iadInvokeSummaryTimer = undefined;
    }, 300);
  }
}

function noteIadEvaluation(hitClientCache: boolean): void {
  iadEvalCount++;
  if (hitClientCache) iadHitCount++;
  else iadMissCount++;

  if (iadEvalSummaryTimer === undefined) {
    iadEvalSummaryTimer = globalThis.setTimeout(() => {
      devLog(
        `[IAD] evaluated ${iadEvalCount} formula${iadEvalCount === 1 ? '' : 's'} → ` +
        `${iadHitCount} served from client cache, ${iadMissCount} missed → re-fetching from API`
      );
      iadEvalCount = 0;
      iadHitCount = 0;
      iadMissCount = 0;
      iadEvalSummaryTimer = undefined;
    }, 300);
  }
}

async function resolveIADValue(
  ticker: string,
  period?: string,
  ltCode?: string,
  ltType?: string,
  dp?: any
): Promise<any> {
  // Immediate synchronous log to confirm function invocation
  devLog('[IAD] ========== FUNCTION INVOKED ==========');
  devLog('[IAD] Function called with params:', {
    ticker,
    period,
    ltCode,
    ltType,
    dp,
    timestamp: new Date().toISOString(),
  });

  try {
    if (dp === '-') {
      return '-';
    }

    if (!ticker) {
      return '-ERROR: Ticker required';
    }

    // if (true){
    //   return "1234"
    // }

    const tickerOptions = parseTickerOptions(ticker);
    const fullKey = buildIADKey(tickerOptions.ticker, period, ltCode, ltType, dp);
    devLog(`[IAD] Built key: ${fullKey}`);

    if (ltCode?.toUpperCase().includes('HEADING')) {
      return '';
    }

    if (fullKey.includes('#LOADING')) {
      return '#LOADING';
    }

    if (!tickerOptions.bypassCache) {
      const cachedValue = DataStore.get(fullKey);
      if (cachedValue !== undefined) {
        noteIadEvaluation(true);
        return formatValue(cachedValue, ltType, dp);
      }
    }

    // Cache miss (or an explicit bypass): this key will be re-fetched via the
    // batch path. After a Refresh cleared the cache, every re-invoked formula
    // lands here, which is what drives the re-fetch.
    noteIadEvaluation(false);
    return await queueBatchRequest(fullKey, ltType, dp);
  } catch (error) {
    console.error('[IAD] ERROR processing request:', error);
    logger.error('IAD function error', error as Error);
    return '-ERROR';
  }
}

function parseTickerOptions(ticker: string): { ticker: string; bypassCache: boolean } {
  const rawTicker = ticker || '';
  const markerIndex = rawTicker.toUpperCase().indexOf(NO_CACHE_MARKER);

  if (markerIndex < 0) {
    return { ticker: rawTicker, bypassCache: false };
  }

  return {
    ticker: rawTicker.slice(0, markerIndex),
    bypassCache: true,
  };
}

function queueBatchRequest(fullKey: string, ltType?: string, dp?: any): Promise<any> {
  startBatchSession(); // Track when first function call happens
  return new Promise((resolve) => {
    const resolvers = batchQueue.get(fullKey) || [];
    const isNewQueuedKey = resolvers.length === 0;
    resolvers.push({ resolve, ltType, dp });
    batchQueue.set(fullKey, resolvers);
    if (isNewQueuedKey) {
      totalKeysQueuedForInsertClick++;
      iad5KeysSeen++;
    }
    scheduleBatchFlush();
    // Issue 5: start the stall watchdog and snapshot the queue as it grows
    // (first key, then every 5000 distinct keys) so a wedge is visible in the
    // localStorage mirror without flooding the console.
    iad5StartStallWatchdog();
    if (isNewQueuedKey && (batchQueue.size === 1 || totalKeysQueuedForInsertClick % 5000 === 0)) {
      iad5Log('QUEUE');
    }
  });
}

function scheduleBatchFlush(): void {
  if (batchTimer !== undefined) {
    return;
  }

  batchTimer = globalThis.setTimeout(() => {
    batchTimer = undefined;
    flushBatchQueue();
  }, BATCH_DEBOUNCE_MS);
}

function flushBatchQueue(): void {
  if (batchQueue.size > 0) {
    const workloadKeyCount = Math.max(batchQueue.size, totalKeysQueuedForInsertClick);
    const plan = chooseBatchPlan(workloadKeyCount);
    currentBatchSize = plan.batchSize;
    currentMaxConcurrentBatches = plan.maxConcurrentBatches;
    const planKey = `${currentBatchSize}/${currentMaxConcurrentBatches}`;
    if (planKey !== lastLoggedBatchPlan) {
      devLog(`[IAD] [BATCH TIMING] Batch plan: queued=${batchQueue.size}, insertClickQueued=${totalKeysQueuedForInsertClick}, batchSize=${currentBatchSize}, max parallelism=${currentMaxConcurrentBatches}, maxBatchCap=${MAX_BATCH_SIZE_LIMIT}`);
      // Issue 5 (batch sizing): the plan is chosen from `workloadKeyCount`, which
      // is the LARGER of the instantaneous queue and the cumulative distinct keys
      // seen so far — NOT the (unknown, still-arriving) grand total. Excel feeds
      // streaming invocations gradually, so early flushes see a small number and
      // pick 4000; the size only climbs to 7000/10000 once the cumulative count
      // crosses 30k/90k. This log makes the ramp visible: watch `workload` grow
      // and `batchSize` step up. If it never reaches 10000, the cumulative count
      // never crossed 90k (waves too small, or the queue drained between waves).
      iad5Log(
        'PLAN',
        `workload=${workloadKeyCount} (queuedNow=${batchQueue.size}, cumulativeSeen=${totalKeysQueuedForInsertClick}) → batchSize=${currentBatchSize}, maxConcurrent=${currentMaxConcurrentBatches}`
      );
      lastLoggedBatchPlan = planKey;
    }
  }

  // Spin up batches up to the concurrency limit. Each batch carves
  // MAX_BATCH_SIZE keys off the head of the queue and runs independently.
  let startedThisFlush = 0;
  while (inFlightBatchCount < currentMaxConcurrentBatches && batchQueue.size > 0) {
    const keys = Array.from(batchQueue.keys()).slice(0, currentBatchSize);
    if (keys.length === 0) break;

    inFlightBatchCount++;
    totalBatchesStarted++;
    startedThisFlush++;
    const batchNumber = totalBatchesStarted;

    const resolverMap = new Map<string, PendingResolver[]>();
    keys.forEach((key) => {
      resolverMap.set(key, batchQueue.get(key) || []);
      batchQueue.delete(key);
    });

    runBatch(batchNumber, keys, resolverMap);
  }

  // Issue 5: a flush that starts NO batch while keys remain queued is the
  // deadlock signature — almost always concurrency saturated by in-flight
  // batches that never completed. Distinguish it from a healthy flush.
  if (batchQueue.size > 0 && startedThisFlush === 0) {
    iad5Log(
      'FLUSH_NOOP',
      inFlightBatchCount >= currentMaxConcurrentBatches
        ? `saturated: inFlight=${inFlightBatchCount} >= max=${currentMaxConcurrentBatches}, ${batchQueue.size} keys waiting`
        : `no batch started despite ${batchQueue.size} queued (unexpected)`
    );
  } else if (startedThisFlush > 0) {
    iad5Log('FLUSH_STARTED', `started=${startedThisFlush}, remaining=${batchQueue.size}`);
  }

  // If the queue is empty AND no batches are running, close the session.
  if (sessionActive && inFlightBatchCount === 0 && batchQueue.size === 0) {
    endBatchSession();
  }
}

async function runBatch(
  batchNumber: number,
  keys: string[],
  resolverMap: Map<string, PendingResolver[]>
): Promise<void> {
  const batchStartTime = performance.now();
  devLog(`[IAD] [BATCH TIMING] Batch #${batchNumber} started: ${keys.length} keys, inFlight=${inFlightBatchCount}, queueRemaining=${batchQueue.size}`);
  iad5KeysInFlight += keys.length;

  try {
    const fetchStartTime = performance.now();
    const results = await fetchBatch(keys);
    const fetchTime = performance.now() - fetchStartTime;
    const resultMap = new Map<string, any>();

    results.forEach((item) => {
      if (item?.Key && item.Value !== undefined) {
        DataStore.set(item.Key, item.Value);
        resultMap.set(item.Key, item.Value);
      }
    });

    resolverMap.forEach((resolvers, key) => {
      if (!resultMap.has(key)) {
        resolvers.forEach(({ resolve }) => resolve(''));
        return;
      }
      const value = resultMap.get(key);
      resolvers.forEach(({ resolve, ltType, dp }) => {
        resolve(formatValue(value, ltType, dp));
      });
    });

    const batchTotalTime = performance.now() - batchStartTime;
    totalBatchesCompleted++;
    totalKeysFetched += keys.length;
    totalKeysMatched += resultMap.size;
    totalBatchFetchTime += fetchTime;
    slowestBatchFetchTime = Math.max(slowestBatchFetchTime, fetchTime);

    devLog(`[IAD] [BATCH TIMING] Batch #${batchNumber} completed:`, {
      keysRequested: keys.length,
      keysMatched: resultMap.size,
      fetchTimeMs: fetchTime.toFixed(2),
      totalBatchTimeMs: batchTotalTime.toFixed(2),
      remainingInQueue: batchQueue.size,
      inFlight: inFlightBatchCount - 1,
    });
  } catch (error) {
    console.error(`[IAD] [BATCH TIMING] Batch #${batchNumber} error:`, error);
    // Issue 5: AbortError == the 60s BATCH_TIMEOUT_MS fired (the call hung);
    // anything else is a server/parse error. Both resolve the cells to #ERROR,
    // so if cells stay #BUSY forever this branch was NOT reached.
    const isTimeout = (error as Error)?.name === 'AbortError';
    iad5Log('BATCH_ERROR', `#${batchNumber} ${isTimeout ? 'TIMEOUT (hung >60s)' : (error as Error)?.message?.slice(0, 120)}`);
    resolverMap.forEach((resolvers) => {
      resolvers.forEach(({ resolve }) => resolve('#ERROR'));
    });
  } finally {
    inFlightBatchCount--;
    // Every key in this batch has now been resolved (a value, '', or #ERROR), so
    // its cell is no longer #BUSY. Track progress for the localStorage counters.
    iad5KeysInFlight -= keys.length;
    iad5KeysResolved += keys.length;
    iad5UpdateCounters();
    // After this batch finishes, try to spin up another if work remains.
    if (batchQueue.size > 0) {
      flushBatchQueue();
    } else if (inFlightBatchCount === 0) {
      endBatchSession();
    }
  }
}

async function fetchBatch(keys: string[]): Promise<Array<{ Key: string; Value: any }>> {
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => controller.abort(), BATCH_TIMEOUT_MS);

  try {
    const usernameFilter = await tokenStorage.getUsernameFilter();
    devLog(usernameFilter)
    if (!usernameFilter) {
      // Issue 5: a missing token means the request is skipped entirely — cells
      // resolve to #ERROR (not #BUSY) and no call reaches the network.
      iad5Log('NO_TOKEN', `${keys.length} keys skipped — no auth token`);
      throw new Error('No auth token available for IAD batch fetch');
    }

    // Issue 5: THE definitive "an API call is actually firing" marker. If cells
    // are #BUSY but this never logs and httpCalls stays 0, the stall is upstream
    // (queue/flush), not the network.
    iad5TotalHttpCalls++;
    iad5LastFetchStartAt = performance.now();
    iad5Log('HTTP_CALL', `POST get_add_in_data | keys=${keys.length}`);

    const batchEndpoint = `/consensus_v2/get_add_in_data?username_filter=${encodeURIComponent(usernameFilter)}`;
    const response = await fetch(batchEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(await getEdgeAuthHeaders()),
        ...(await getInsyncUserHeader()),
      },
      body: JSON.stringify(keys.map((key) => ({ Key: key }))),
      signal: controller.signal,
    });
    devLog("response",response)
    // AWS WAF answered (CAPTCHA/Challenge). Checked BEFORE reading the body:
    // that page embeds the WAF challenge key/context, which must never end up in
    // an error message, a log row or a cell.
    if (isWafBlocked(response)) {
      throw new Error(WAF_BLOCKED_MESSAGE);
    }
    // Session ended server-side. The taskpane (a separate runtime) notices on its
    // own next request; here the formula just says why it has no data.
    if (isSessionEnded(response)) {
      throw new Error(SESSION_ENDED_MESSAGE);
    }
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${response.statusText}${body ? ` - ${body.slice(0, 300)}` : ''}`);
    }

    const text = await response.text();
    const sanitizedText = text
      .replace(/\bNaN\b/g, 'null')
      .replace(/\bInfinity\b/g, 'null')
      .replace(/\b-Infinity\b/g, 'null');
    const data = JSON.parse(sanitizedText);
    return Array.isArray(data) ? data : [];
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

export function buildIADKey(
  ticker: string,
  period?: string,
  ltCode?: string,
  ltType?: string,
  dp?: any
): string {
  const t = (ticker || '').toUpperCase();
  let fp = (period || '').toUpperCase();
  // Spaces stripped before any branch compares against a datapoint name. The
  // Insert Formula pane writes the UI label straight into the formula, so the
  // "Number Format" option produces =IAD_OFJ(t,ltcode,"Number Format","CD") —
  // with a space. Without this the NUMBERFORMAT branch below never matches, and
  // the call falls through to the generic key
  //   AAPL|IS_P_101|NUMBER FORMAT|INSYNC ESTIMATES||
  // which the backend has no row for, so the cell stays empty.
  //
  // Normalised HERE rather than at formula-generation time on purpose: these are
  // live formulas, so fixing the key builder also repairs already-inserted cells
  // on their next recalculation.
  //
  // NOTE: the COM add-in has this same defect — InsertFormulaForm.cs:3469 writes
  // the same spaced label and InSyncAddinMethod.cs:222 compares against
  // "numberformat" with no space-stripping. This deliberately diverges.
  const lc = (ltCode || '').toUpperCase().replace(/\s+/g, '');
  let lt = (ltType || '').toUpperCase();
  const dpValue = normalizeExcelArgument(dp);

  if (lt === 'VALUATION') {
    return DataStore.buildKey(t, fp, lc, 'VALUATION', normalizeIsoDate(dpValue), '', true);
  }

  if (lc === 'PREQDATE') {
    if (lt === 'CD' || lt === 'SD') lt = 'INSYNC ESTIMATES';
    return DataStore.buildKey(t, fp, '', lt, '', 'PREQDATE');
  }

  if (lc === 'SCALER') {
    return DataStore.buildKey(t, '', fp, '', '', 'SCALER');
  }

  if (lc === 'SOURCE') {
    return DataStore.buildKey(t, '', fp, '', '', 'SOURCE');
  }

  if (lc === 'NUMBERFORMAT' || lc === 'LINEITEMCURRENCY') {
    return DataStore.buildKey(t, '', fp, '', '', lc);
  }

  if (lc === 'REVISIONDATE' || lc.startsWith('REVISIONDATE')) {
    const revisionType = lc.replace('REVISIONDATE', '');
    return DataStore.buildKey(t, fp, '', dpValue || '', revisionType, 'REVISIONDATE');
  }

  if (lc === 'ESTIMATEORACTUAL') {
    return DataStore.buildKey(t, fp, '', '', '', 'ESTIMATEORACTUAL');
  }

  if (lc === 'LINEITEM') {
    return DataStore.buildKey(t, '', fp, '', '', 'LINEITEM');
  }

  if (fp === 'LASTUPDATE') {
    return DataStore.buildKey(t, '', '', '', '', 'LASTUPDATE');
  }

  if (fp === 'COMPANYNAME') {
    return DataStore.buildKey(t, '', '', '', '', 'COMPANYNAME');
  }

  if (fp === 'FYE') {
    return DataStore.buildKey(t, '', '', '', '', 'FYE');
  }

  if (fp === 'TICKERCURRENCY') {
    return DataStore.buildKey(t, '', '', '', '', 'TICKERCURRENCY');
  }

  if (fp === 'TICKERCURRENCYDETAIL') {
    return DataStore.buildKey(t, '', '', '', '', 'TICKERCURRENCYDETAIL');
  }

  if (fp === 'LASTREPORTED' && !lc) {
    return buildFiscalCodeKey(t, 'LRQ');
  }

  if (lc === 'UPDATEDATE' || lc === 'EARNINGDATE') {
    return DataStore.buildKey(t, fp, '', '', '', 'EARNINGDATE');
  }

  if (lc === 'UIC') {
    return DataStore.buildKey(t, '', fp, '', '', 'UIC');
  }

  if (lc === 'LTCODE') {
    return DataStore.buildKey(t, '', fp, '', '', 'LTCODE');
  }

  if (lc === 'QED') {
    return DataStore.buildKey(t, fp, '', '', '', 'QED');
  }

  if (!lc && isFiscalCode(fp)) {
    return buildFiscalCodeKey(t, fp);
  }

  if (lc === 'RECEIVEDDATE') {
    return DataStore.buildKey(t, '', '', fp, '', 'RECEIVEDDATE');
  }

  if (lc === 'ANALYSTNAME') {
    return DataStore.buildKey(t, '', '', fp, '', 'ANALYSTNAME');
  }

  if (fp.includes('CY')) {
    if (!fp.includes('CY-')) {
      fp = fp.replace('CY', 'CY-');
    }
    if (USE_API_DOC_KEY_FORMAT) {
      // Pos 4 is BROKER and pos 5 is UPDATEDATE. This branch used to pass the
      // ISO date as the 4th argument, so a calendarized key came out as
      //   AAPL|CY-2025|IS_P_101|2026-06-30|||     (date sitting in BROKER)
      // leaving UPDATEDATE empty and the broker unusable. The VALUATION branch
      // above already does this correctly; this now matches it.
      const cyBroker = lt === 'CD' || lt === 'SD' ? 'INSYNC ESTIMATES' : lt;
      return DataStore.buildKey(t, fp, lc, cyBroker, normalizeIsoDate(dpValue), '', true);
    }
    return DataStore.buildKey(t, fp, lc, normalizeIsoDate(dpValue), '', '', true);
  }

  if (lt === 'CD' || lt === 'SD') {
    lt = 'INSYNC ESTIMATES';
  }

  return DataStore.buildKey(t, fp, lc, lt, dpValue, '');
}

/**
 * Key for a fiscal-code lookup such as `=INSYNC.IAD_OFJ("AAPL","LRQ")`.
 *
 * The `FISCALCODES` marker belongs in position 6 (DATAPOINT), NOT position 5
 * (UPDATEDATE). Mirrors COM's `BuildFullKey(t, "", "", "", "FiscalCodes", code)`:
 * "FiscalCodes" does not parse as a date, so it drops through to pos6 and pos5
 * is left blank.
 *
 * Verified against consensus_v2/get_add_in_data on 2026-08-21:
 *   AAPL||||FISCALCODES||LRQ  -> "-"      (pos5 — what this used to send)
 *   AAPL|||||FISCALCODES|LRQ  -> "3Q26"   (pos6 — correct)
 *
 * Hand-built rather than via DataStore.buildKey because buildKey routes its `dp`
 * argument to pos5/pos6 by date-detection, and a literal marker needs pos6
 * unconditionally.
 */
function buildFiscalCodeKey(ticker: string, code: string): string {
  return [ticker, '', '', '', '', 'FISCALCODES', code].join('|');
}

function isFiscalCode(value: string): boolean {
  return [
    'LRQ',
    'NEQ',
    'SEQ',
    'TEQ',
    'FEQ',
    'PQ',
    'SPQ',
    'TPQ',
    'FPQ',
    'LRY',
    'NEY',
    'SEY',
    'TEY',
    'FEY',
    'PY',
    'SPY',
    'TPY',
    'FPY',
  ].includes(value.toUpperCase());
}

/**
 * yyyy-MM-dd for the Valuation and CY key slots, matching IAD/BuildFullKey in the
 * COM add-in (InSyncAddinMethod.cs): `DateTime.TryParse(DP, out d) ? d.ToString("yyyy-MM-dd") : ""`.
 *
 * Two details this has to keep in step with the COM source of truth:
 *  - Format from the LOCAL date parts. toISOString() converts to UTC first, which
 *    rolls the date back a day in any timezone ahead of UTC (IST included) and
 *    yields a key the backend has no row for.
 *  - A DP that doesn't parse as a date becomes "", not the original text, so a
 *    stray "E"/"A" here can't leak into the key.
 */
function normalizeIsoDate(value?: string): string {
  if (!value) return '';
  const date = parseCellDate(value);
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

// Excel serial window ~1954-2119: wide enough for any fiscal date, and clear of the
// small integers that legitimately appear as datapoints (revision numbers, etc).
const EXCEL_SERIAL_MIN = 20000;
const EXCEL_SERIAL_MAX = 80000;

/**
 * The serial behind a date cell, or null if this isn't one.
 *
 * Excel gives a custom function a date cell as its serial number, and IAD_OFJ
 * declares dp as `string` (functions.json), so Excel stringifies that serial before
 * we ever see it: "46082", never "3/1/2026". The COM add-in's UDF takes a string
 * too, but COM coerces a date cell to its formatted text, so DateTime.TryParse saw a
 * real date and BuildFullKey routed it to the key's 5th slot. Without this, the
 * stringified serial fails the date test and lands in the 6th slot as literal text —
 * a key the backend has no row for.
 *
 * Declaring dp as `any` would also surface the raw number, but `any`-typed
 * parameters are entangled with the AppSource allowCustomDataForDataTypeAny issue,
 * so recognise the string instead.
 */
export function excelDateSerial(value: any): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > EXCEL_SERIAL_MIN && value < EXCEL_SERIAL_MAX ? value : null;
  }
  const text = String(value ?? '').trim();
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
  const serial = Number(text);
  return Number.isFinite(serial) && serial > EXCEL_SERIAL_MIN && serial < EXCEL_SERIAL_MAX ? serial : null;
}

function normalizeExcelArgument(value: any): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return formatDateSlash(value);
  const serial = excelDateSerial(value);
  if (serial !== null) return formatDateSlash(excelSerialToDate(serial));
  return String(value).trim();
}

export function parseCellDate(value: string): Date | null {
  const trimmed = value.trim();
  if (/^[+-]?\d+(?:\.\d+)?$/.test(trimmed)) {
    return null;
  }

  const mdY = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:\s.*)?$/);
  if (mdY) {
    const year = Number(mdY[3].length === 2 ? `20${mdY[3]}` : mdY[3]);
    const date = new Date(year, Number(mdY[1]) - 1, Number(mdY[2]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (iso) {
    const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // Only fall back to the lenient JS Date parser for strings that actually
  // look like a date (contain a month name). Otherwise V8 fuzzy-parses
  // arbitrary text — e.g. `new Date("Convert 1")` -> 1/1/2001 — whereas the
  // COM add-in's DateTime.TryParse rejects it and keeps the text. COM is the
  // source of truth, so free-form line-item values must stay as text.
  if (!MONTH_NAME_RE.test(trimmed)) {
    return null;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Serial -> local midnight of that calendar date. The serial is a UTC-based day
 * count, so the instant it names must be read back with UTC getters and rebased to
 * local; formatting the raw instant with local getters (formatDateSlash) would shift
 * the date back a day for anyone behind UTC.
 */
export function excelSerialToDate(serial: number): Date {
  const utc = new Date(Math.floor(serial - 25569) * 86400 * 1000);
  return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
}

function formatDateSlash(date: Date): string {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

function formatValue(value: any, ltType?: string, dp?: any): any {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    if (dp?.toLowerCase() === 'date' || ltType?.toLowerCase() === 'date') {
      return value;
    }
    return value.toLocaleDateString('en-US');
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (value === '-' || value.startsWith('Error:') || value === '#LOADING') {
      return value;
    }

    if (/^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i.test(trimmedValue)) {
      const num = Number(trimmedValue);
      if (Number.isFinite(num)) {
        return num;
      }
    }

    const dpText = normalizeExcelArgument(dp).toLowerCase();
    const ltTypeText = normalizeExcelArgument(ltType).toLowerCase();
    const shouldReturnDate = dpText === 'date' || ltTypeText === 'date';
    const parsedDate = parseCellDate(trimmedValue);
    if (parsedDate) {
      return shouldReturnDate ? parsedDate : formatDateSlash(parsedDate);
    }

    return value;
  }

  if (typeof value === 'number') {
    return value;
  }

  return value;
}

export async function IAD2(
  ticker: string,
  period: string,
  ltCode?: string,
  ltType?: string,
  dp?: any
): Promise<any> {
  return resolveIADValue(ticker, period, ltCode, ltType, dp);
}

/**
 * InSync Line Data — spills a line item table for a ticker/selector.
 *
 * Ported to match COM `InSyncAddinMethod.ILD` (line 820). The Office.js version
 * previously spilled a SINGLE column of LT codes with no headers; COM spills a
 * FOUR column table with a title row and a header row:
 *
 *   row 0:  "AAPL => ALLLINEITEM" |          |            |
 *   row 1:                        | LTCode   | UIC code   | LineItem
 *   row 2+:                       | <ltcode> | <uic>      | <line item>
 *
 * Column 0 is intentionally blank — COM's comment: "first column left blank
 * intentionally (so visible data starts in next cell)".
 *
 * @customfunction ILD_OFJ
 * @helpurl https://insyncanalytics.com/consensus/add-in/functions/#ild-ofj
 * @param ticker Ticker symbol.
 * @param selector Line item selector, such as AllLineItem, AllLineItem.IS, AllLineItem.BS, AllLineItem.CF, AllLineItem.SD.OM, or AllLineItem.KPI.
 * @returns A 4-column table: blank, LTCode, UIC code, LineItem.
 */
export async function ILD_OFJ(ticker: string, selector: string = 'AllLineItem'): Promise<string[][]> {
  // Evaluating an InSync formula counts as using the add-in (OTP idle timeout).
  recordAddinActivity();
  const cleanTicker = String(ticker || '').trim().toUpperCase();
  const cleanSelector = String(selector || 'AllLineItem').trim();

  // COM returns a distinguishable message rather than a blank cell, so a typo'd
  // ticker, an empty result and a backend failure are not all rendered the same.
  if (!cleanTicker) {
    return [['#ERROR: missing ticker']];
  }

  // Matched against the WHOLE selector, as COM does. Splitting on '.' and taking
  // the last segment loses information: "AllLineItem.SD.OM" reduces to "OM",
  // which is why the SD+KM pair below used to collapse to SD alone.
  const selectorKey = cleanSelector.toLowerCase();

  try {
    const response = await fetch(`/consensus/query_uic_meta?ticker=${encodeURIComponent(cleanTicker)}&is_inferred=true`, {
      headers: {
        Accept: 'application/json',
        'Accept-Encoding': 'gzip, deflate',
        'User-Agent': config.USER_AGENT || 'InsyncAddinRequester',
        ...(await getEdgeAuthHeaders()),
        ...(await getInsyncUserHeader()),
      },
    });

    if (isWafBlocked(response)) {
      // Not "No data": the request never reached the API.
      return [['Blocked by a network security check. Try again in a few minutes.']];
    }
    if (isSessionEnded(response)) {
      return [[SESSION_ENDED_MESSAGE]];
    }
    if (!response.ok) {
      return [['No data']];
    }

    const data = await response.json();
    const ltCodes = Array.isArray(data?.LTCODE) ? data.LTCODE : Array.isArray(data?.LTCode) ? data.LTCode : [];
    const categories = Array.isArray(data?.CATEGORY_CODE) ? data.CATEGORY_CODE : [];
    const uics = Array.isArray(data?.UIC) ? data.UIC : [];
    const lineItems = Array.isArray(data?.LINEITEM) ? data.LINEITEM : [];
    const kpiSequences = Array.isArray(data?.KPISEQUENCE) ? data.KPISEQUENCE : [];

    if (ltCodes.length === 0) {
      return [['No data']];
    }

    // Category sets per selector, mirroring COM's explicit branches. Every branch
    // also drops heading rows.
    const categoryFilters: Record<string, string[]> = {
      'alllineitem.is': ['IS'],
      'alllineitem.bs': ['BS'],
      'alllineitem.cf': ['CF'],
      // COM keeps SD *or* KM here. The old selector parsing mapped OM -> ['SD']
      // only, silently dropping every KM row (348 of 719 for AAPL).
      'alllineitem.sd.om': ['SD', 'KM'],
    };
    const allowedCategories = categoryFilters[selectorKey] || [];
    const isKpiSelector = selectorKey === 'alllineitem.kpi';

    const rows: string[][] = [];

    ltCodes.forEach((code: any, index: number) => {
      const ltCode = String(code || '').trim();
      if (!ltCode || ltCode.toLowerCase().includes('heading')) return;

      if (isKpiSelector) {
        // COM filters on KPISEQUENCE being present — NOT on CATEGORY_CODE.
        //
        // Previous oiadin implementation, kept for reference:
        //   KPI: ['KPI', 'KM']   // matched CATEGORY_CODE instead
        //   if (allowedCategories.length > 0 && !allowedCategories.includes(itemCategory)) return;
        //
        // These are not equivalent. For AAPL: KPISEQUENCE yields 32 items,
        // CATEGORY_CODE in (KPI,KM) yields 348 — 380 items differ. There is no
        // 'KPI' category code in the data at all, so the old rule was really
        // "all KM rows".
        const sequence = kpiSequences[index];
        if (sequence === null || sequence === undefined || String(sequence).trim() === '') return;
      } else if (allowedCategories.length > 0) {
        const itemCategory = String(categories[index] || '').toUpperCase();
        if (!allowedCategories.includes(itemCategory)) return;
      }

      rows.push([
        '',
        ltCode,
        String(uics[index] ?? ''),
        String(lineItems[index] ?? ''),
      ]);
    });

    if (rows.length === 0) {
      return [['No data']];
    }

    return [
      [`${cleanTicker} => ${cleanSelector.toUpperCase()}`, '', '', ''],
      ['', 'LTCode', 'UIC code', 'LineItem'],
      ...rows,
    ];
  } catch (error) {
    logger.error('Failed to fetch ILD line items', error as Error, { ticker: cleanTicker, selector: cleanSelector });
    return [['No data']];
  }
}

export default IAD_OFJ;

CustomFunctions.associate("IAD_OFJ", IAD_OFJ);
CustomFunctions.associate("ILD_OFJ", ILD_OFJ);